import React, { useState, useMemo, useEffect } from 'react';
import { Download, ChevronDown, ChevronUp } from 'lucide-react';
import { content, getText } from '../data/content';
import { TAG_STYLES, FILTER_BUTTON_STYLES } from '../data/content-publications';
import { theme, combineClasses, getCardStyle, getButtonStyle } from '../theme/theme';
import Modal from '../components/Modal';

/** TOC 无图或图片加载失败（如有链接但无文件）时使用的占位图路径 */
const TOC_PLACEHOLDER = '/images/publications/TOC-waiting.png';

/**
 * ============================================================================
 * 页面：发表论文 - Publications.jsx
 * ============================================================================
 *
 * 【作用】发表论文列表页：标题区、筛选（All/Synthesis/Measurement/Theory）、搜索框、论文卡片列表；点击卡片打开详情弹窗（作者/期刊/DOI/下载/TOC 图）。
 * 【如何修改】论文数据在 src/data/content-publications.js 的 papers 数组；筛选标签与 categories 一致；样式来自 theme。
 *
 * 【参数说明】props: lang（"en"|"cn"）, themeMode（"light"|"dark"）。
 * 内部状态：filter（分类筛选）、yearFilter（年份筛选）、searchTerm（搜索词）、selectedPaper（当前选中的论文）、showBefore2021（是否展开 2021 年前折叠）。
 *
 * 【扩展功能说明】
 * - 多标签：每篇论文使用 tags 数组，可同时拥有多个标签（如 Measurement + Synthesis）；标签颜色由 content-publications.js 的 TAG_STYLES 统一配置。
 * - TOC 占位图：无 toc 地址或 toc 地址对应文件不存在（加载失败）时均显示占位图（路径见 TOC_PLACEHOLDER）；弹窗右侧仅展示 TOC 图片，不显示「TOC」标题文字，图片尺寸略大。
 * - 2021 年前折叠：year < 2021 的论文单独放在页面底部折叠区「2021 年以前 / Before 2021」，点击可展开/收起。
 * - 年份筛选：在分类按钮旁增加年份下拉框，可选「全部年份」或具体年份，与分类、搜索共同过滤列表。
 */
const PublicationsView = ({ lang, themeMode = "dark" }) => {
  const isLight = themeMode === "light";
  const [filter, setFilter] = useState("All");
  const [yearFilter, setYearFilter] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  /** 是否展开「2021 年以前」折叠区域 */
  const [showBefore2021, setshowBefore2021] = useState(false);
  const [selectedPaper, setSelectedPaper] = useState(null);
  /** 当前论文的 TOC 图片是否加载失败（有链接但无文件）；切换论文时重置，用于与无地址时统一走占位图 */
  const [tocLoadFailed, setTocLoadFailed] = useState(false);
  useEffect(() => {
    setTocLoadFailed(false);
  }, [selectedPaper?.id]);

  // 根据当前语言将论文的 title、journal 转为对应语言
  const papers = useMemo(() => {
    return content.publications.papers.map(paper => ({
      ...paper,
      title: typeof paper.title === 'object' ? paper.title[lang] || paper.title.en : paper.title,
      journal: typeof paper.journal === 'object' ? paper.journal[lang] || paper.journal.en : paper.journal
    }));
  }, [lang]);

  const categories = ["All", "Synthesis", "Measurement", "Theory"];

  /** 标签展示文案：Synthesis/Measurement/Theory 用中英文，Before 2021 用 Before2021Label */
  const getTagLabel = (tag, lang) => {
    if (tag === "Before 2021") return getText('publications.Before2021Label', lang);
    return getText(`publications.filterCategories.${tag}`, lang) ?? tag;
  };

  /** 从论文列表提取所有年份（降序），供年份筛选下拉使用 */
  const yearOptions = useMemo(() => {
    const years = [...new Set(papers.map((p) => p.year))].sort((a, b) => b - a);
    return years;
  }, [papers]);

  // 过滤论文：根据分类、年份和搜索词；支持多标签（任一标签匹配即视为匹配分类）
  const filteredPapers = useMemo(() => {
    return papers.filter((paper) => {
      const tags = Array.isArray(paper.tags) ? paper.tags : [];
      const matchesFilter = filter === "All" || tags.includes(filter);
      const matchesYear = yearFilter === "All" || paper.year === Number(yearFilter);
      const matchesSearch = searchTerm === "" ||
        paper.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        paper.authors.toLowerCase().includes(searchTerm.toLowerCase()) ||
        paper.journal.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesFilter && matchesYear && matchesSearch;
    });
  }, [papers, filter, yearFilter, searchTerm]);

  /** 主列表：2021 年及以后的论文 */
  const papersMain = useMemo(() => filteredPapers.filter((p) => p.year >= 2021), [filteredPapers]);
  /** 折叠区：2021 年以前的论文 */
  const papersBefore2021 = useMemo(() => filteredPapers.filter((p) => p.year < 2021), [filteredPapers]);

  return (
    <>
    <div className="min-h-screen pt-8 pb-20 px-4 max-w-6xl mx-auto animate-fade-in">
      <div className={combineClasses(
        "flex flex-col md:flex-row justify-between items-center md:items-end mb-12 border-b pb-8 gap-4",
        isLight ? "border-gray-200" : "border-white/10"
      )}>
        <div className="w-full md:w-auto text-center md:text-left">
        <h2 className={combineClasses(
          "text-4xl font-bold mb-2",
          isLight ? "text-gray-900" : "text-white"
        )}>{getText('publications.title', lang)}</h2>
        <p className={isLight ? "text-gray-600" : "text-gray-400"}>{getText('publications.subtitle', lang)}</p>
        </div>
        <div className="relative">
          <input
            type="text"
            placeholder={getText('publications.searchPlaceholder', lang)}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={combineClasses(
              "border text-sm rounded-full focus:ring-cyan-500 focus:border-cyan-500 block w-full md:w-64 px-4 py-2.5 backdrop-blur-sm transition-colors",
              isLight 
                ? "bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400" 
                : "bg-white/5 border-white/10 text-gray-300 placeholder-gray-500"
            )}
          />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-10">
        {categories.map((cat) => {
          const isSelected = filter === cat;
          const btnStyles = cat === "All"
            ? (isSelected
                ? (isLight ? "bg-gray-900 text-white" : "bg-white text-black")
                : (isLight ? "bg-gray-100 text-gray-600 hover:bg-gray-200" : "bg-white/5 text-gray-400 hover:bg-white/10"))
            : (FILTER_BUTTON_STYLES[cat]
                ? (isSelected ? FILTER_BUTTON_STYLES[cat].selected[isLight ? "light" : "dark"] : FILTER_BUTTON_STYLES[cat].unselected[isLight ? "light" : "dark"])
                : (isLight ? "bg-gray-100 text-gray-600" : "bg-white/5 text-gray-400"));
          return (
            <button
              key={cat}
              onClick={() => setFilter(cat)}
              className={combineClasses("px-4 py-2 rounded-lg text-sm font-bold transition-all border", btnStyles)}
            >
              {cat === "All" ? getText('publications.filterAll', lang) : (getText(`publications.filterCategories.${cat}`, lang) ?? cat)}
            </button>
          );
        })}
        {/* 年份筛选下拉：全部 / 具体年份 */}
        <select
          value={yearFilter}
          onChange={(e) => setYearFilter(e.target.value)}
          className={combineClasses(
            "px-4 py-2 rounded-lg text-sm font-bold border cursor-pointer transition-colors",
            isLight
              ? "bg-gray-50 border-gray-200 text-gray-900"
              : "bg-white/5 border-white/10 text-gray-300"
          )}
          aria-label={lang === 'cn' ? '按年份筛选' : 'Filter by year'}
        >
          <option value="All">{getText('publications.filterAllYears', lang)}</option>
          {yearOptions.map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
      </div>

      <div className="space-y-4">
        {/* 主列表：2021 年及以后的论文 */}
        {papersMain.map((paper, index) => (
          <div
            key={paper.id ?? index}
            className={combineClasses(
              "group relative backdrop-blur-md rounded-xl p-6 hover:border-cyan-500/30 transition-all duration-300 cursor-pointer border",
              getCardStyle(themeMode)
            )}
            onClick={() => setSelectedPaper(paper)}
          >
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2 flex-wrap">
                  <span className={combineClasses(
                    "text-xl font-bold font-mono",
                    isLight ? "text-gray-400" : "text-white/20"
                  )}>
                    {paper.year}
                  </span>
                  {(Array.isArray(paper.tags) ? paper.tags : []).map((tag) => {
                    const style = TAG_STYLES[tag] || { color: "text-gray-400", bg: "bg-gray-400/10" };
                    return (
                      <span
                        key={tag}
                        className={combineClasses(
                          "px-2 py-0.5 rounded text-xs font-bold border",
                          style.bg,
                          style.color,
                          isLight ? "border-gray-200" : "border-white/5"
                        )}
                      >
                        {getTagLabel(tag, lang)}
                      </span>
                    );
                  })}
                </div>
                <h3 className={combineClasses(
                  "text-lg font-bold mb-1 transition-colors",
                  isLight 
                    ? "text-gray-900 group-hover:text-cyan-600" 
                    : "text-white group-hover:text-cyan-300"
                )}>
                  {paper.title}
                </h3>
                <p className={combineClasses(
                  "text-sm",
                  isLight ? "text-gray-600" : "text-gray-400"
                )}>
                  {paper.authors} -{" "}
                  <span className="font-medium italic">{paper.journal}</span>
                </p>
              </div>
              {paper.pdf ? (
                <a
                  href={paper.pdf}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={combineClasses(
                    "p-2 rounded-full transition-colors",
                    isLight 
                      ? "bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-900" 
                      : "bg-white/5 hover:bg-white hover:text-black text-gray-400"
                  )}
                  onClick={(e) => e.stopPropagation()}
                  title={lang === 'cn' ? '下载 PDF' : 'Download PDF'}
                  aria-label={lang === 'cn' ? '下载 PDF' : 'Download PDF'}
                >
                  <Download className="w-5 h-5" />
                </a>
              ) : null}
            </div>
          </div>
        ))}

        {/* 2021 年以前的论文：折叠区，可展开/收起 */}
        {papersBefore2021.length > 0 && (
          <div className={combineClasses(
            "rounded-xl border overflow-hidden",
            isLight ? "border-gray-200 bg-gray-50/50" : "border-white/10 bg-white/5"
          )}>
            <button
              type="button"
              onClick={() => setshowBefore2021(!showBefore2021)}
              className={combineClasses(
                "w-full flex items-center justify-between px-6 py-4 text-left font-bold transition-colors",
                isLight ? "text-gray-900 hover:bg-gray-100" : "text-white hover:bg-white/10"
              )}
              aria-expanded={showBefore2021}
            >
              <span>{getText('publications.Before2021Label', lang)}</span>
              {showBefore2021 ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>
            {showBefore2021 && (
              <div className={combineClasses(
                "space-y-4 p-4 pt-0 border-t",
                isLight ? "border-gray-200" : "border-white/10"
              )}>
                {papersBefore2021.map((paper, index) => (
                  <div
                    key={paper.id ?? index}
                    className={combineClasses(
                      "group relative backdrop-blur-md rounded-xl p-6 hover:border-cyan-500/30 transition-all duration-300 cursor-pointer border",
                      getCardStyle(themeMode)
                    )}
                    onClick={() => setSelectedPaper(paper)}
                  >
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2 flex-wrap">
                          <span className={combineClasses(
                            "text-xl font-bold font-mono",
                            isLight ? "text-gray-400" : "text-white/20"
                          )}>
                            {paper.year}
                          </span>
                          {(Array.isArray(paper.tags) ? paper.tags : []).map((tag) => {
                            const style = TAG_STYLES[tag] || { color: "text-gray-400", bg: "bg-gray-400/10" };
                            return (
                              <span
                                key={tag}
                                className={combineClasses(
                                  "px-2 py-0.5 rounded text-xs font-bold border",
                                  style.bg,
                                  style.color,
                                  isLight ? "border-gray-200" : "border-white/5"
                                )}
                              >
                                {getTagLabel(tag, lang)}
                              </span>
                            );
                          })}
                        </div>
                        <h3 className={combineClasses(
                          "text-lg font-bold mb-1 transition-colors",
                          isLight 
                            ? "text-gray-900 group-hover:text-cyan-600" 
                            : "text-white group-hover:text-cyan-300"
                        )}>
                          {paper.title}
                        </h3>
                        <p className={combineClasses(
                          "text-sm",
                          isLight ? "text-gray-600" : "text-gray-400"
                        )}>
                          {paper.authors} -{" "}
                          <span className="font-medium italic">{paper.journal}</span>
                        </p>
                      </div>
                      {paper.pdf ? (
                        <a
                          href={paper.pdf}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={combineClasses(
                            "p-2 rounded-full transition-colors",
                            isLight 
                              ? "bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-900" 
                              : "bg-white/5 hover:bg-white hover:text-black text-gray-400"
                          )}
                          onClick={(e) => e.stopPropagation()}
                          title={lang === 'cn' ? '下载 PDF' : 'Download PDF'}
                          aria-label={lang === 'cn' ? '下载 PDF' : 'Download PDF'}
                        >
                          <Download className="w-5 h-5" />
                        </a>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>

    {/* 论文详情模态框 */}
    {selectedPaper && (
      <Modal
        isOpen={!!selectedPaper}
        onClose={() => setSelectedPaper(null)}
        title={selectedPaper?.title || ''}
        size="lg"
        themeMode={themeMode}
      >
        <div className="space-y-6">
          {/* 仅显示年份与标签，标题已在 Modal 头部展示 */}
          <div className={combineClasses(
            "flex items-center gap-4 pb-4 border-b flex-wrap",
            isLight ? "border-gray-200" : "border-white/10"
          )}>
            <span className={combineClasses(
              "text-xl font-bold font-mono",
              isLight ? "text-gray-400" : "text-white/20"
            )}>
              {selectedPaper.year}
            </span>
            {(Array.isArray(selectedPaper.tags) ? selectedPaper.tags : []).map((tag) => {
              const style = TAG_STYLES[tag] || { color: "text-gray-400", bg: "bg-gray-400/10" };
              return (
                <span
                  key={tag}
                  className={combineClasses(
                    "px-3 py-1 rounded text-xs font-bold border",
                    style.bg,
                    style.color,
                    isLight ? "border-gray-200" : ""
                  )}
                >
                  {getTagLabel(tag, lang)}
                </span>
              );
            })}
          </div>

          {/* 左右布局：左侧元信息，右侧 TOC 图；无地址或加载失败时用占位图，右侧不显示「TOC」文字，仅放图且更大 */}
          {(() => {
            const hasToc = !!(selectedPaper.toc && !tocLoadFailed);
            const showTocArea = selectedPaper.toc || true;
            return (
              <div className={combineClasses(
                "flex flex-col md:flex-row gap-6",
                showTocArea ? "md:gap-8" : ""
              )}>
                <div className={showTocArea ? "flex-1 min-w-0" : ""}>
                  <p className={combineClasses(
                    "mb-2",
                    isLight ? "text-gray-700" : "text-gray-300"
                  )}>
                    <strong className={isLight ? "text-gray-900" : "text-white"}>Authors:</strong> {selectedPaper.authors}
                  </p>
                  <p className={combineClasses(
                    "mb-2",
                    isLight ? "text-gray-700" : "text-gray-300"
                  )}>
                    <strong className={isLight ? "text-gray-900" : "text-white"}>Journal:</strong> <span className="italic">{selectedPaper.journal}</span>
                  </p>
                  {selectedPaper.doi && (
                    <p className={combineClasses(
                      "mb-4",
                      isLight ? "text-gray-700" : "text-gray-300"
                    )}>
                      <strong className={isLight ? "text-gray-900" : "text-white"}>DOI:</strong> {selectedPaper.doi}
                    </p>
                  )}
                  <div className={combineClasses(
                    "flex gap-4 pt-2",
                    !showTocArea ? "border-t " + (isLight ? "border-gray-200" : "border-white/10") : ""
                  )}>
                    {selectedPaper.pdf ? (
                      <a
                        href={selectedPaper.pdf}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={combineClasses(
                          getButtonStyle(themeMode, 'secondary'),
                          "inline-flex items-center gap-2",
                          isLight ? "text-gray-900" : "text-white"
                        )}
                      >
                        <Download className={combineClasses(
                          "w-4 h-4",
                          isLight ? "text-gray-900" : "text-white"
                        )} />
                        {lang === 'cn' ? '下载 PDF' : 'Download PDF'}
                      </a>
                    ) : null}
                  </div>
                </div>
                {/* TOC 区域：无标题文字，仅图片；无地址或 onError 时用占位图，尺寸更大（md:w-[20rem] lg:w-[28rem] max-h-[36rem]） */}
                <div className={combineClasses(
                  "flex-shrink-0 md:w-[20rem] lg:w-[28rem] h-full",
                  "rounded-lg overflow-hidden border",
                  isLight ? "border-gray-200 bg-gray-50" : "border-white/10 bg-white/5"
                )}>
                  {/* 有 toc 且未加载失败时用论文图，否则占位图；onError 时置 tocLoadFailed 以统一走占位图 */}
                  <img
                    src={hasToc ? selectedPaper.toc : TOC_PLACEHOLDER}
                    alt="TOC"
                    className="w-full h-auto object-contain max-h-[36rem]"
                    onError={() => setTocLoadFailed(true)}
                  />
                </div>
              </div>
            );
          })()}
        </div>


      </Modal>
    )}
    </>
  );
};

export default PublicationsView;
